import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, Mail, Smartphone, CreditCard, Star, AlertTriangle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface NotificationPreferences {
  id: string;
  user_id: string;
  email_notifications: boolean;
  system_alerts: boolean;
  security_alerts: boolean;
  revenue_alerts: boolean;
  user_activity_alerts: boolean;
  notification_frequency: 'real_time' | 'daily' | 'weekly';
}

export const NotificationPreferencesPage: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch notification preferences
  const { data: preferences, isLoading } = useQuery({
    queryKey: ['notification-preferences', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await backendClient
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return data as NotificationPreferences | null;
    },
    enabled: !!user,
  });

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await backendClient
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          ...updates,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences', user?.id] });
      toast({
        title: "Success",
        description: "Notification preferences updated",
      });
    },
    onError: (error) => {
      console.error('Failed to update preferences:', error);
      toast({
        title: "Error",
        description: "Failed to update notification preferences",
        variant: "destructive",
      });
    },
  });

  const handleToggle = (field: keyof NotificationPreferences, value: boolean | string) => {
    updatePreferencesMutation.mutate({ [field]: value });
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <div className="space-y-6">
          <div className="text-center">
            <div className="animate-pulse bg-muted h-8 w-64 mx-auto rounded mb-2"></div>
            <div className="animate-pulse bg-muted h-4 w-96 mx-auto rounded"></div>
          </div>
          <div className="grid gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse bg-muted h-48 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const currentPreferences = preferences ?? {
    email_notifications: true,
    system_alerts: true,
    security_alerts: true,
    revenue_alerts: true,
    user_activity_alerts: false,
    notification_frequency: 'real_time' as const,
  };

  return (
    <div className="container max-w-4xl mx-auto py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Notification Preferences</h1>
          <p className="text-muted-foreground">
            Customize how and when you receive notifications about your VoiceThera practice
          </p>
        </div>

        {/* Email Notifications Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              <CardTitle>Email Notifications</CardTitle>
            </div>
            <CardDescription>
              Receive important updates and alerts via email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-base font-medium">Enable Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive credit alerts, milestone achievements, and monthly updates
                </p>
              </div>
              <Switch
                checked={currentPreferences.email_notifications}
                onCheckedChange={(checked) => handleToggle('email_notifications', checked)}
                disabled={updatePreferencesMutation.isPending}
              />
            </div>
          </CardContent>
        </Card>

        {/* In-App Notifications Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-600" />
              <CardTitle>In-App Notifications</CardTitle>
            </div>
            <CardDescription>
              Real-time notifications while using the platform
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-medium">System Alerts</Label>
                  <Badge variant="secondary">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Important
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Platform updates, maintenance notices, and system status
                </p>
              </div>
              <Switch
                checked={currentPreferences.system_alerts}
                onCheckedChange={(checked) => handleToggle('system_alerts', checked)}
                disabled={updatePreferencesMutation.isPending}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-medium">Credit & Revenue Alerts</Label>
                  <Badge variant="outline">
                    <CreditCard className="h-3 w-3 mr-1" />
                    Financial
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Low credit warnings, milestone achievements, and earnings updates
                </p>
              </div>
              <Switch
                checked={currentPreferences.revenue_alerts}
                onCheckedChange={(checked) => handleToggle('revenue_alerts', checked)}
                disabled={updatePreferencesMutation.isPending}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-medium">Security Alerts</Label>
                  <Badge variant="destructive">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Critical
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Login attempts, account changes, and security warnings
                </p>
              </div>
              <Switch
                checked={currentPreferences.security_alerts}
                onCheckedChange={(checked) => handleToggle('security_alerts', checked)}
                disabled={updatePreferencesMutation.isPending}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-base font-medium">Client Activity</Label>
                <p className="text-sm text-muted-foreground">
                  Session completions, feedback, and client engagement
                </p>
              </div>
              <Switch
                checked={currentPreferences.user_activity_alerts}
                onCheckedChange={(checked) => handleToggle('user_activity_alerts', checked)}
                disabled={updatePreferencesMutation.isPending}
              />
            </div>
          </CardContent>
        </Card>

        {/* Notification Frequency Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-blue-600" />
              <CardTitle>Notification Frequency</CardTitle>
            </div>
            <CardDescription>
              How often you'd like to receive notifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="real_time"
                  name="frequency"
                  value="real_time"
                  checked={currentPreferences.notification_frequency === 'real_time'}
                  onChange={(e) => handleToggle('notification_frequency', e.target.value)}
                  disabled={updatePreferencesMutation.isPending}
                  className="h-4 w-4"
                />
                <Label htmlFor="real_time" className="text-base font-medium cursor-pointer">
                  Real-time
                </Label>
                <Badge variant="secondary">Recommended</Badge>
              </div>
              <p className="text-sm text-muted-foreground ml-6">
                Receive notifications immediately as they occur
              </p>

              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="daily"
                  name="frequency"
                  value="daily"
                  checked={currentPreferences.notification_frequency === 'daily'}
                  onChange={(e) => handleToggle('notification_frequency', e.target.value)}
                  disabled={updatePreferencesMutation.isPending}
                  className="h-4 w-4"
                />
                <Label htmlFor="daily" className="text-base font-medium cursor-pointer">
                  Daily Digest
                </Label>
              </div>
              <p className="text-sm text-muted-foreground ml-6">
                Receive a daily summary of all notifications
              </p>

              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="weekly"
                  name="frequency"
                  value="weekly"
                  checked={currentPreferences.notification_frequency === 'weekly'}
                  onChange={(e) => handleToggle('notification_frequency', e.target.value)}
                  disabled={updatePreferencesMutation.isPending}
                  className="h-4 w-4"
                />
                <Label htmlFor="weekly" className="text-base font-medium cursor-pointer">
                  Weekly Summary
                </Label>
              </div>
              <p className="text-sm text-muted-foreground ml-6">
                Receive a weekly summary of important notifications
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            disabled={updatePreferencesMutation.isPending}
            onClick={() => toast({ 
              title: "Settings Saved", 
              description: "Your notification preferences are automatically saved" 
            })}
          >
            {updatePreferencesMutation.isPending ? 'Saving...' : 'Preferences Saved'}
          </Button>
        </div>
      </div>
    </div>
  );
};
